import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRightIcon } from './icons';
import styles from './ui.module.css';

/**
 * Tone is a reading, not decoration: `critical` means somebody has to do
 * something today, `warning` that something is running out of time. It now
 * colours the icon rather than the whole tile — the tile itself stays white.
 */
type Tone = 'default' | 'accent' | 'ok' | 'warning' | 'critical';

const TONE_CLASS: Record<Tone, string | undefined> = {
  default: undefined,
  accent: styles.tileAccent,
  ok: styles.tileOk,
  warning: styles.tileWarning,
  critical: styles.tileCritical,
};

interface StatTileProps {
  label: string;
  value: string;
  note?: string;
  tone?: Tone;
  /** A line icon, tinted by `tone`, shown in the tile's corner. */
  icon?: ReactNode;
  /** When given, the whole tile becomes the link to wherever the number leads. */
  to?: string;
}

export function StatTile({ label, value, note, tone = 'default', icon, to }: StatTileProps) {
  const classes = [styles.tile, TONE_CLASS[tone], to ? styles.tileLink : null]
    .filter(Boolean)
    .join(' ');

  const body = (
    <>
      {(icon || to) && (
        <div className={styles.tileTop}>
          {icon && <span className={styles.tileIcon}>{icon}</span>}
          {/* The chevron is the affordance: it marks the tile as something you
              can open, which a plain figure does not. */}
          {to && <ChevronRightIcon className={styles.tileChevron} />}
        </div>
      )}
      <p className={styles.tileLabel}>{label}</p>
      <p className={styles.tileValue}>{value}</p>
      {note && <p className={styles.tileNote}>{note}</p>}
    </>
  );

  if (!to) return <div className={classes}>{body}</div>;

  return (
    <Link to={to} className={classes}>
      {body}
    </Link>
  );
}

/** The row the tiles sit in — a grid that wraps rather than scrolls. */
export function StatTiles({ children }: { children: ReactNode }) {
  return <div className={styles.tiles}>{children}</div>;
}
